import { useLayoutEffect, useRef, useState } from 'react';
import { StepScheduleAdmin } from '@/components';
import { cn } from '@/lib/utils';
import {
	ProfessionalPatientSelectPanel,
	type ProfessionalPatientSelectPanelHandle,
} from './ProfessionalPatientSelectPanel';

/** Altura fixa do card de paciente no mobile (scroll interno). Agenda fica com altura natural. */
const MOBILE_PATIENT_CARD_CLASS =
	'max-lg:flex max-lg:h-[28rem] max-lg:max-h-[28rem] max-lg:shrink-0 max-lg:flex-col';

const DESKTOP_SCHEDULE_MIN_WIDTH = '(min-width: 1200px)';

/**
 * Passo 1 (PRO): wrap paciente | agenda (data e horário no mesmo card).
 * No desktop a coluna da agenda define a altura; o painel de paciente
 * acompanha esse tamanho com scroll interno — nunca empurra a página.
 */
const ProfessionalBookSessionStep = () => {
	const scheduleColumnRef = useRef<HTMLDivElement>(null);
	const patientPanelRef = useRef<ProfessionalPatientSelectPanelHandle>(null);
	const [scheduleColumnHeight, setScheduleColumnHeight] = useState<number>();

	useLayoutEffect(() => {
		const column = scheduleColumnRef.current;
		if (!column) return;

		const desktopQuery = window.matchMedia(DESKTOP_SCHEDULE_MIN_WIDTH);

		const syncHeight = () => {
			if (!desktopQuery.matches) {
				setScheduleColumnHeight(undefined);
				return;
			}
			setScheduleColumnHeight(column.offsetHeight);
		};

		syncHeight();

		const observer = new ResizeObserver(syncHeight);
		observer.observe(column);
		desktopQuery.addEventListener('change', syncHeight);

		return () => {
			observer.disconnect();
			desktopQuery.removeEventListener('change', syncHeight);
		};
	}, []);

	const patientPanelStyle =
		scheduleColumnHeight !== undefined
			? ({
					['--patient-panel-h' as string]: `${scheduleColumnHeight}px`,
			  } as React.CSSProperties)
			: undefined;

	return (
		<StepScheduleAdmin
			hideProfessionalAvailabilityHeader
			scheduleLayout="stacked"
			splitScheduleBlocks
			footerPosition="full-width"
			onOpenPatientWhatsAppEdit={() =>
				patientPanelRef.current?.openEditScrollToWhatsApp()
			}
			renderInLayout={({ date, footer }) => (
				<div className="flex w-full flex-col gap-3">
					<div className="flex w-full flex-col gap-3 min-[1200px]:flex-row min-[1200px]:items-start min-[1200px]:gap-3">
						<div
							className={cn(
								'flex w-full shrink-0 flex-col overflow-visible min-[1200px]:min-h-0 min-[1200px]:w-[min(32rem,42%)] min-[1200px]:max-w-[32rem]',
								MOBILE_PATIENT_CARD_CLASS,
							)}
							style={patientPanelStyle}
						>
							<ProfessionalPatientSelectPanel
								ref={patientPanelRef}
								className="max-lg:h-full max-lg:min-h-0 max-lg:flex-1 min-[1200px]:h-[var(--patient-panel-h)] min-[1200px]:max-h-[var(--patient-panel-h)] min-[1200px]:min-h-0"
							/>
						</div>

						<div
							ref={scheduleColumnRef}
							className="flex w-full min-w-0 flex-col items-stretch min-[1200px]:min-w-[min(100%,18rem)] min-[1200px]:flex-1"
						>
							{date}
						</div>
					</div>

					<div className="w-full shrink-0">{footer}</div>
				</div>
			)}
		/>
	);
};

export { ProfessionalBookSessionStep };
